import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { observer } from 'mobx-react';
import { compose } from 'recompose';
import PropTypes from 'prop-types';
import moment from 'moment';
import format from 'string-template';

import UserActivity, { ACTIVITY_PAGE_SIZE } from '../../../../Model/UserActivity';
import Select from '../../../../../components/Select';
import LoadingPage from '../../../../../components/LoadingPage';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import { DateFormat } from '../../../../../utils/date';
import ActivityList from '../ActivityListView';

import { getActivityContent, activityCategories } from './utils';
import {
  Container,
  FilterWrapper,
} from './styles';
import texts from './texts';

const ACTIVITIES_PER_PAGE = 10;
const ALL_ACTIVITY_TYPE = 'ALL';

const filterOptions = Object.entries(activityCategories)
  .map(([category, { label }]) => ({
    value: category,
    label,
  }));

const ClientHistory = ({ user, coachId }) => {
  const [limit, setLimit] = useState(ACTIVITIES_PER_PAGE);
  /*
    One entry per fetched page, in order, rather than a single collection: the
    query is bounded now, so "the client's activities" is however many pages have
    been asked for so far. Mirrors the chat module's `messageCollectionList`.
  */
  const [activityPages, setActivityPages] = useState([]);
  const [hasMoreActivities, setHasMoreActivities] = useState(true);
  const [selectedFilter, setSelectedFilter] = useState(ALL_ACTIVITY_TYPE);
  const [isReady, setIsReady] = useState(false);

  const isComponentMountedRef = useComponentMounted();
  /*
    Synchronous, unlike state, so two load-more triggers in the same tick cannot
    both start a fetch and append the same page twice.
  */
  const isFetchingRef = useRef(false);
  /*
    Which filter the in-flight request belongs to.

    A filter change must always issue a request, so it cannot share the in-flight
    guard above — coalescing it would silently drop the reset and leave the tab
    on the previous filter's rows. And the request it supersedes must not write
    its page into state afterwards, because that page was queried under the
    filter the coach just left.
  */
  const requestRef = useRef(0);

  // Only when a category is selected; `ALL` deliberately queries no type clause,
  // so an event type the server adds later still appears here.
  const selectedTypes = useMemo(() => (
    selectedFilter === ALL_ACTIVITY_TYPE ? null : activityCategories[selectedFilter].types
  ), [selectedFilter]);

  const fetchPage = useCallback(async ({ after = null, replace = false }) => {
    // Only "load more" is coalesced; a filter reset always issues its request.
    if (!replace && isFetchingRef.current) {
      return;
    }
    // A reset supersedes whatever was in flight; a load-more joins the current filter.
    const requestId = replace ? requestRef.current + 1 : requestRef.current;
    requestRef.current = requestId;
    isFetchingRef.current = true;
    try {
      const page = await UserActivity.getActivitiesByUser(user, coachId, {
        startAfterDoc: after,
        types: selectedTypes,
      });
      // Superseded by a later filter, or unmounted: drop the page rather than
      // rendering rows for a filter the coach is no longer on.
      if (!isComponentMountedRef.current || requestId !== requestRef.current) {
        return;
      }
      // A short page means the query is exhausted, which is what ends paging.
      setHasMoreActivities(page.docs.length >= ACTIVITY_PAGE_SIZE);
      setActivityPages((previousPages) => (replace ? [page] : [...previousPages, page]));
    } finally {
      // A superseded request must not release the guard the live one holds, nor
      // flip the tab out of its loading state on the live one's behalf.
      if (requestId === requestRef.current) {
        isFetchingRef.current = false;
        if (isComponentMountedRef.current) {
          setIsReady(true);
        }
      }
    }
  }, [
    user,
    selectedTypes,
    coachId,
    isComponentMountedRef,
  ]);

  // First page, and a fresh first page whenever the filter changes, since the
  // filter is part of the query rather than applied to what is already loaded.
  useEffect(() => {
    setActivityPages([]);
    setHasMoreActivities(true);
    setLimit(ACTIVITIES_PER_PAGE);
    setIsReady(false);
    fetchPage({ replace: true });
  }, [fetchPage]);

  const loadedActivities = useMemo(
    () => activityPages.flatMap((page) => page.docs.slice()),
    [activityPages],
  );

  /*
    Sliced before the map, not after: the reveal step is smaller than the page
    size, so mapping everything loaded would rebuild every row — each with its
    own `getActivityContent` and moment formatting — to reveal ten more. The
    loaded count lives on `loadedActivities` instead, which is what paging needs.
  */
  const activities = useMemo(() => loadedActivities
    .slice(0, limit)
    .map((activity) => {
      const { content, subContent } = getActivityContent(activity);
      return {
        key: activity.id,
        tag: moment(activity.createdAt).format(DateFormat.DATE_FORMAT_COMMA),
        content,
        subContent,
        type: activity.type,
      };
    }), [loadedActivities, limit]);

  const lastLoadedDoc = useMemo(
    () => loadedActivities[loadedActivities.length - 1] ?? null,
    [loadedActivities],
  );

  /*
    Reveal ten more rows per click, as before. A click only costs a query when
    the rows it wants have not been fetched yet, which is why the page size is
    larger than the reveal step.
  */
  useEffect(() => {
    if (isReady && limit > loadedActivities.length && hasMoreActivities && lastLoadedDoc) {
      fetchPage({ after: lastLoadedDoc });
    }
  }, [
    isReady,
    limit,
    loadedActivities.length,
    hasMoreActivities,
    lastLoadedDoc,
    fetchPage,
  ]);

  const canLoadMore = useMemo(
    () => loadedActivities.length > limit || hasMoreActivities,
    [loadedActivities.length, limit, hasMoreActivities],
  );

  const handleFilter = useCallback((event) => {
    setSelectedFilter(event.target.value);
  }, []);

  const handleLoadMore = useCallback(() => {
    setLimit((prevLimit) => prevLimit + ACTIVITIES_PER_PAGE);
  }, []);

  if (!isReady) {
    return <LoadingPage />;
  }

  return (
    <Container>
      <FilterWrapper>
        <Select
          options={[
            { value: ALL_ACTIVITY_TYPE, label: texts.filterOptions[ALL_ACTIVITY_TYPE] },
            ...filterOptions,
          ]}
          value={selectedFilter}
          onChange={handleFilter}
          label={texts.filterLabel}
        />
      </FilterWrapper>
      <ActivityList
        activities={activities}
        canLoadMore={canLoadMore}
        handleLoadMore={handleLoadMore}
        emptyText={format(texts.emptyText, {
          filter: selectedFilter === ALL_ACTIVITY_TYPE
            ? texts.filterOptions[ALL_ACTIVITY_TYPE]
            : activityCategories[selectedFilter].label,
        })}
      />
    </Container>
  );
};

ClientHistory.propTypes = {
  coachId: PropTypes.string.isRequired,
  user: PropTypes.string.isRequired,
};

export default compose(
  observer,
)(ClientHistory);
